import React, { useEffect, useState } from 'react';
import { ACCENT, ACCENT_LIGHT, TEXT_DARK, TEXT_MID, TEXT_LIGHT, FONT } from '../lib/tokens';
import { Ic } from '../components/Icons';
import { Card, ScreenWrap, Tag } from '../components/ui/Primitives';
import { supabase, hasSupabase } from '../lib/supabase';
import { useBreakpoint } from '../lib/useBreakpoint';

const FALLBACK_SUMMARIES = [
  {
    id: 's1',
    num: 'Session 1',
    title: 'Succession Planning',
    summary:
      'The group reached consensus that most state associations lack documented succession plans. Key takeaways: build a leadership pipeline 3–5 years out, create a shadow-board program for emerging leaders, and document institutional knowledge before transitions occur.',
  },
  {
    id: 's2',
    num: 'Session 2',
    title: 'Governance & Structure',
    summary:
      'Participants explored flat vs. layered governance models. Consensus favored streamlined boards (9–12 members) with working committees rather than large advisory councils.',
  },
  {
    id: 's3',
    num: 'Session 3',
    title: 'Membership Strategy',
    summary:
      'Data-driven membership management was the theme. Attendees committed to implementing a member health score by Q4. AI-assisted onboarding was identified as the single highest-ROI investment for associations under 500 members.',
  },
];

export default function PostEventScreen({ isPostEvent }) {
  const [openTab, setOpenTab] = useState('summaries');
  const [summaries, setSummaries] = useState(FALLBACK_SUMMARIES);
  const [commitments, setCommitments] = useState([]);
  const [ideas, setIdeas] = useState([]);

  useEffect(() => {
    if (!hasSupabase || !isPostEvent) return;
    supabase
      .from('session_summaries')
      .select('*')
      .order('session_num')
      .then(({ data }) => data?.length && setSummaries(data));
    supabase
      .from('commitments')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data }) => setCommitments(data || []));
    supabase
      .from('parking_lot')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data }) => setIdeas(data || []));
  }, [isPostEvent]);

  if (!isPostEvent) {
    return (
      <ScreenWrap>
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            padding: '40px 20px',
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: 'rgba(255,255,255,0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 4,
            }}
          >
            <Ic.Shield size={24} color="rgba(255,255,255,0.3)" />
          </div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: 'rgba(255,255,255,0.6)',
              textAlign: 'center',
            }}
          >
            Available Post-Event
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.35)',
              textAlign: 'center',
              lineHeight: 1.55,
              maxWidth: 260,
            }}
          >
            Session summaries, commitment archives, and the resource library will unlock after the
            event concludes.
          </div>
        </div>
      </ScreenWrap>
    );
  }

  const tabs = [
    { id: 'summaries', label: 'Summaries' },
    { id: 'commitments', label: 'Commitments' },
    { id: 'parking', label: 'Parking Lot' },
  ];

  const content = (
    <>
      {openTab === 'summaries' &&
        summaries.map((s) => (
          <Card key={s.id} style={{ padding: '14px 15px' }}>
            <Tag style={{ marginBottom: 8 }}>{s.num}</Tag>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: TEXT_DARK,
                margin: '8px 0 8px',
              }}
            >
              {s.title}
            </div>
            <p style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6, margin: 0 }}>
              {s.summary}
            </p>
          </Card>
        ))}
      {openTab === 'commitments' &&
        commitments.map((c) => (
          <ArchiveCard key={c.id} name={c.name} text={c.commitment} meta={`Due ${c.by_when}`} />
        ))}
      {openTab === 'parking' &&
        ideas.map((item) => <ArchiveCard key={item.id} name={item.name} text={item.idea} />)}
    </>
  );

  const bp = useBreakpoint();

  // ── Desktop: sidebar tabs on the left, content panel on the right ──────
  if (bp === 'desktop') {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '220px 1fr',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {tabs.map((t) => {
            const active = openTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setOpenTab(t.id)}
                style={{
                  textAlign: 'left',
                  padding: '10px 14px',
                  border: 'none',
                  background: active ? 'rgba(27,108,181,0.18)' : 'transparent',
                  color: active ? '#fff' : 'rgba(255,255,255,0.7)',
                  fontFamily: FONT,
                  fontSize: 13.5,
                  fontWeight: active ? 600 : 500,
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{content}</div>
      </div>
    );
  }

  // ── Mobile / tablet: top tab bar + scrollable list (original) ──────────
  return (
    <ScreenWrap noPad>
      <div
        style={{
          display: 'flex',
          overflowX: 'auto',
          gap: 4,
          padding: '0 18px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          marginBottom: 0,
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setOpenTab(t.id)}
            style={{
              whiteSpace: 'nowrap',
              padding: '7px 13px',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 8,
              fontSize: 12.5,
              fontWeight: 600,
              fontFamily: FONT,
              background: openTab === t.id ? ACCENT : 'transparent',
              color: openTab === t.id ? '#fff' : 'rgba(255,255,255,0.4)',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ padding: '12px 18px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {content}
      </div>
    </ScreenWrap>
  );
}

function ArchiveCard({ name, text, meta }) {
  return (
    <Card style={{ padding: '13px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: ACCENT_LIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 700, color: ACCENT }}>
            {name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_DARK }}>{name}</div>
          {meta && <div style={{ fontSize: 11, color: TEXT_LIGHT }}>{meta}</div>}
        </div>
      </div>
      <p style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.55, margin: 0 }}>{text}</p>
    </Card>
  );
}
